import { useCallback } from 'react';

import { useIntl } from 'react-intl-next';
import { createHook, createStore } from 'react-sweet-state';

import type { DocNode } from '@atlaskit/adf-schema';

import { appendADFContent } from '../../common/utils/adf';

import messages from './messages';

type State = {
	highlightActionsEditorPlaceholder: DocNode | null;
};

const state = createStore({
	initialState: {
		highlightActionsEditorPlaceholder: null,
	} as State,
	actions: {
		setHighlightActionsEditorPlaceholder:
			(highlightActionsEditorPlaceholder: State['highlightActionsEditorPlaceholder']) =>
			({ setState }) => {
				setState({ highlightActionsEditorPlaceholder });
			},
	},
});

// exported for testing
export const useHighlightActionsEditorPlaceholderStore = createHook(state);

const getHighlightActionPlaceholderData = (prompt: DocNode, placeholderText: string) => {
	const promptContent = appendADFContent(prompt, placeholderText);
	return promptContent;
};

export const useHighlightActionsEditorPlaceholder = () => {
	const [{ highlightActionsEditorPlaceholder }, { setHighlightActionsEditorPlaceholder }] =
		useHighlightActionsEditorPlaceholderStore();
	const { formatMessage } = useIntl();

	const insertHighlightActionsEditorPlaceholder = useCallback(
		(prompt: DocNode): void => {
			const highlightActionsPlaceholderPrompt = formatMessage(
				messages.highlightActionsPlaceholderPrompt,
			);
			const placeholderData = getHighlightActionPlaceholderData(
				prompt,
				highlightActionsPlaceholderPrompt,
			);

			setHighlightActionsEditorPlaceholder(placeholderData);
		},
		[setHighlightActionsEditorPlaceholder, formatMessage],
	);

	const clearHighlightActionsEditorPlaceholder = useCallback((): void => {
		setHighlightActionsEditorPlaceholder(null);
	}, [setHighlightActionsEditorPlaceholder]);

	return {
		highlightActionsEditorPlaceholder,
		insertHighlightActionsEditorPlaceholder,
		clearHighlightActionsEditorPlaceholder,
	};
};
